<template>
    <div class='pa-5'>
        <v-card v-if='data'>
            <v-card-title>
                <h1 class='title'>{{ 'Mine Areas' | term }}</h1>
                <v-btn small color='secondary' class='ml-3' @click='create()'>
                    <v-icon left size='medium'>mdi-plus</v-icon>
                    {{ $t('shared.button.add') }}
                </v-btn>
                <v-spacer></v-spacer>
            </v-card-title>
            <v-data-table disable-pagination hide-default-footer :headers='headers' :items='data'>
                <template v-slot:item.id='{ item }'>
                    <v-btn text icon @click='edit(item)' class='float-right'>
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn text icon @click='editSubscribers(item)' class='float-right'>
                        <v-icon>mdi-bell-plus</v-icon>
                    </v-btn>
                    <v-btn text icon @click='editShiftSchedule(item)' class='float-right' :title="$t('mine-areas.shift-schedule')">
                        <v-icon>mdi-calendar-clock</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>
        <standard-dialog :render='!!selected' :value='showModal' :max-width='900' persistent>
            <template v-slot:title>
                <span v-if='selected.id'>{{ $t('shared.button.edit') }}</span>
                <span v-else>{{ $t('shared.button.create') }}</span>
                <span class='ml-1'>{{ 'Mine Area' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref='form' :lazy-validation='true'>
                    <v-container fluid>
                        <v-row>
                            <v-col cols='12' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('shared.label.name')"
                                    :rules='[$rules.required(), $rules.maxLength(50)]'
                                    v-model='selected.name'
                                    counter='50'
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='12' class='pa-2 title'>{{ $t('mine-areas.shift-timings') }}</v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='4' class='pa-2'>
                                <v-select
                                    :label="$t('mine-areas.shifts-per-day')"
                                    v-model='selected.numberOfShiftsPerDay'
                                    :rules='[$rules.required()]'
                                    :items='[1, 2, 3, 4]'
                                    @change='shiftsChanged()'
                                    :menu-props='{ dark: true }'
                                ></v-select>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('mine-areas.first-shift-starts-at')"
                                    :rules='[$rules.required(), $rules.time()]'
                                    v-model='selected.firstShiftStartsAt'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    type='number'
                                    :label="$t('mine-areas.shift-overlap-minutes')"
                                    :rules="[
                                            $rules.min(0),
                                            $rules.max(180),
                                            $rules.multipleOf(15, '15 minute increments only'),
                                        ]"
                                    v-model.number='selected.shiftOverlapMinutes'
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='4' class='pa-2'>
                                <v-select
                                    :label="$t('mine-areas.first-day-of-week')"
                                    v-model='selected.firstDayOfWeek'
                                    :rules='[$rules.required()]'
                                    item-value='id'
                                    item-text='name'
                                    :items='daysOfWeek'
                                    :menu-props='{ dark: true }'
                                ></v-select>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-menu v-model='dateDialog' max-width='290px' :close-on-content-click='false'>
                                    <template v-slot:activator='{ on }'>
                                        <v-text-field
                                            v-model='formattedDate'
                                            :label="$t('mine-areas.start-counting-weeks-from')"
                                            readonly
                                            name='date'
                                            v-on='on'
                                            required
                                            clearable
                                            :rules='[$rules.required()]'
                                            append-icon='mdi-calendar-today'
                                            @click:append='dateDialog = true'
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        dark
                                        v-model='selected.startWeekCountFrom'
                                        class='elevation-0'
                                        min='2019-01-01'
                                        no-title
                                        @change='dateDialog = false'
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols='4' class='pa-2'>
                                <v-select
                                    :label="$t('mine-areas.time-zone')"
                                    v-model='selected.timeZone'
                                    :rules='[$rules.required()]'
                                    item-value='value'
                                    item-text='label'
                                    :items='timezones'
                                    :menu-props='{ dark: true }'
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row v-if="selected.new">
                            <v-col cols='3' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('mine-areas.shift-1-name')"
                                    v-model='selected.shiftNameFirst'
                                    :rules='[$rules.required()]'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='3' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :disabled='selected.numberOfShiftsPerDay < 2'
                                    :label="$t('mine-areas.shift-2-name')"
                                    v-model='selected.shiftNameSecond'
                                    :rules='[$rules.requiredIf(() => selected.numberOfShiftsPerDay > 1)]'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='3' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :disabled='selected.numberOfShiftsPerDay < 3'
                                    :label="$t('mine-areas.shift-3-name')"
                                    v-model='selected.shiftNameThird'
                                    :rules='[$rules.requiredIf(() => selected.numberOfShiftsPerDay > 2)]'
                                ></v-text-field>
                            </v-col>
                            <v-col cols='3' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :disabled='selected.numberOfShiftsPerDay < 4'
                                    :label="$t('mine-areas.shift-4-name')"
                                    v-model='selected.shiftNameFourth'
                                    :rules='[$rules.requiredIf(() => selected.numberOfShiftsPerDay > 3)]'
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row v-if='selected.id'>
                            <v-col cols='12' class='pa-2 title'>{{ $t('mine-areas.logo') }}</v-col>
                            <v-col cols='4'>
                                <v-hover v-slot:default='{ hover }'>
                                    <v-img
                                        :src='imageSource'
                                        :gradient="
                                                hover || imageError
                                                    ? 'to top right, rgba(255,201,14,.8),  rgba(88,223,85,.7), rgba(79,167,255,.8)'
                                                    : null
                                            "
                                        :max-height='74'
                                        :max-width='250'
                                        :min-height='74'
                                        :min-width='200'
                                        @error='imageError = true'
                                        @load='imageError = false'
                                        contain
                                    >
                                        <v-container fill-height pa-0 v-show='hover || imageError'>
                                            <v-layout row wrap align-center class='ma-0'>
                                                <v-flex class='text-center text-uppercase white--text'>
                                                    <div>
                                                        <span v-if="$wait.is('uploading logo')">{{ $t('mine-areas.sending') }}</span>
                                                        <v-btn
                                                            v-else
                                                            outlined
                                                            class='white--text'
                                                            @click='$refs.inputUpload.click()'
                                                        >{{ $t('mine-areas.upload-logo') }}
                                                        </v-btn
                                                        >
                                                    </div>
                                                    <input
                                                        v-show='false'
                                                        ref='inputUpload'
                                                        accept='image/png'
                                                        type='file'
                                                        @change='setImage'
                                                    />
                                                </v-flex>
                                            </v-layout>
                                        </v-container>
                                    </v-img>
                                </v-hover>
                            </v-col>
                            <v-col cols='8'>
                                <v-alert color='grey lighten-2'>
                                    {{ $t('mine-areas.logo-dimensions-hint') }}
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='12' class='pa-2 title'>{{ 'Locations' | term }}</v-col>
                        </v-row>
                        <v-row>
                            <v-col cols='6' class='pa-2'>
                                <v-text-field
                                    autocomplete='off'
                                    :label="$t('mine-areas.location-name-convention')"
                                    v-model='selected.locationConvention'
                                    :rules="[
                                            $rules.required(),
                                            (x) => (locationTestValid().badExpression ? $t('mine-areas.bad-format') : true),
                                        ]"
                                    :hint="$t('mine-areas.location-convention-hint')"
                                    persistent-hint
                                ></v-text-field>
                            </v-col>
                            <v-col cols='6' class='pa-2'>
                                <v-btn
                                    text
                                    color='secondary'
                                    @click='showLocationHelp = true'
                                    v-if='!showLocationHelp'
                                    class='mt-3'
                                >{{ $t('mine-areas.show-help') }}
                                </v-btn
                                >

                                <v-alert class='ml-5 pa-0' v-if='showLocationHelp'>
                                    <v-text-field
                                        class='mb-5'
                                        autocomplete='off'
                                        :label="$t('mine-areas.test-location-pattern')"
                                        v-model='testpattern'
                                        :error='!!testpattern && !locationTestValid().valid'
                                        :placeholder="$t('mine-areas.enter-location-name-to-test')"
                                        counter='50'
                                        :append-icon="locationTestValid() ? 'mdi-check-circle' : 'mdi-cancel'"
                                    ></v-text-field>
                                    <pre class='mono-help grey--text'>{{ $t('mine-areas.location-pattern-help') }}</pre
                                    >
                                </v-alert>
                                <v-btn
                                    text
                                    color='secondary'
                                    @click='showLocationHelp = false'
                                    v-if='showLocationHelp'
                                >{{ $t('mine-areas.hide-help') }}
                                </v-btn
                                >
                            </v-col>
                            <v-row>
                                <v-col cols='12' class='pa-2 title'>{{ $t('mine-areas.configuration') }}</v-col>
                                <v-col cols='6' class='pa-2'>
                                    <v-select
                                        :label="$t('mine-areas.temperature-scale')"
                                        v-model='selected.temperatureScale'
                                        :items='temperatureScales'
                                        item-value='id'
                                        item-text='name'
                                        :readonly='!!selected.id'
                                        :menu-props='{ dark: true }'
                                    ></v-select>
                                </v-col>
                                <v-col cols='6' class='pa-2' v-if='selected.id'>
                                    <v-btn

                                        text
                                        color='secondary'
                                        @click='showChangeTemperatureScale'
                                    >{{ $t('mine-areas.change-temperature-scale') }}
                                    </v-btn
                                    >
                                </v-col>
                            </v-row>
                        </v-row>
                    </v-container>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='selected && selected.id' @click='del()'>{{ $t('shared.button.delete') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='save'>{{ $t('shared.button.save') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <standard-dialog :value='showChangeTemperatureScaleModal' :max-width='400' persistent>
            <template v-slot:title>
                {{ $t('mine-areas.change-temperature-scale') }}
            </template>
            <template v-slot:content>
                <v-form ref='tempScaleForm' :lazy-validation='true'>
                    <span v-if="$wait.is('updating temp scale')">{{ $t('mine-areas.updating') }}</span>
                    <v-select
                        v-else
                        :label="$t('mine-areas.temperature-scale')"
                        v-model='editingTemperatureScale'
                        :items='temperatureScales'
                        item-value='id'
                        item-text='name'
                        :menu-props='{ dark: true }'
                    ></v-select>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showChangeTemperatureScaleModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='changeTemperatureScale'>{{ $t('shared.button.update') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <subscribers-dialog ref='subscribersDialog'></subscribers-dialog>
        <shift-schedule-editor ref='shiftScheduleEditor'></shift-schedule-editor>
        <confirm ref='confirm'></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import AppHttp from '@/lib/AppHttp';
import MineAreas from '@/lib/data/MineAreas';
import dayjs from 'dayjs';
import { DateFormat, TimeZones } from '@/plugins/dates';
import Locations from '@/lib/data/Locations';
import MineAreaStore from '@/lib/stores/MineAreaStore';
import Account from '@/lib/data/Account';
import SubscribersDialog from '@/components/Admin/SubscribersDialog.vue';
import ShiftScheduleEditor from '@/components/Admin/ShiftScheduleEditor.vue';
import StandardDialog from '@/components/Shared/Common/StandardDialog.vue';
import Tenants from '@/lib/Tenants';
import { TemperatureScales } from '@/models/api';
import { enumOptions } from '@/lib/EnumExtensions';
//A = letter, D = digit, . = Anything, * = Many. E.g. `A._D*` or `.* A*`

export default {
    components: { StandardDialog, SubscribersDialog, ShiftScheduleEditor },
    data() {
        return {
            data: null,
            showModal: false,
            showChangeTemperatureScaleModal: false,
            selected: null,
            editingTemperatureScale: null,
            temperatureScales: enumOptions(TemperatureScales, this.$options.filters.temperatureScaleName),
            showLocationHelp: false,
            dateDialog: false,
            testpattern: '',
            imageError: false,
            cacheKey: +new Date(),
            headers: [
                { text: 'Name', value: 'name' },
                { text: this.$t('mine-areas.shift-overlap-mins'), value: 'shiftOverlapMinutes' },
                { text: this.$t('mine-areas.location-names'), value: 'locationConvention' },
                { text: '', value: 'id' }
            ],
            daysOfWeek: [
                { id: 0, name: 'Sunday' },
                { id: 1, name: 'Monday' },
                { id: 2, name: 'Tuesday' },
                { id: 3, name: 'Wednesday' },
                { id: 4, name: 'Thursday' },
                { id: 5, name: 'Friday' },
                { id: 6, name: 'Saturday' }
            ],
            valid: false,
            timezones: []
        };
    },
    async created() {
        this.timezones = TimeZones;
        await this.loadData();
    },
    destroyed() {
    },
    computed: {
        formattedDate: {
            get() {
                if (!this.selected.startWeekCountFrom) {
                    return '';
                }
                if (
                    dayjs(this.selected.startWeekCountFrom).toDate() <
                    dayjs(this.selected.startWeekCountFrom).subtract(10, 'y').toDate()
                ) {
                    return '';
                }
                return dayjs(this.selected.startWeekCountFrom).format(DateFormat.DefaultDisplay);
            },
            set() {
                this.selected.startWeekCountFrom = null;
            }
        },
        imageSource() {
            if (!this.selected || !this.selected.id) return null;
            return AppHttp.urlFromPath(
                `Images/${this.selected.id}_logo.png?viewTenantId=${Tenants.GetCurrentTenant()}&rnd=${this.cacheKey}`
            );
        }
    },
    methods: {
        shiftsChanged() {
            if (this.selected.numberOfShiftsPerDay < 4) {
                this.selected.shiftNameFourth = '';
            }
            if (this.selected.numberOfShiftsPerDay < 3) {
                this.selected.shiftNameThird = '';
            }
            if (this.selected.numberOfShiftsPerDay < 2) {
                this.selected.shiftNameSecond = '';
            }
        },
        async loadData() {
            this.data = await MineAreas.get();
        },
        create() {
            this.edit({
                locationConvention: 'A*D*',
                startWeekCountFrom: dayjs().format(DateFormat.Canonical),
                firstDayOfWeek: 1,
                temperatureScale: TemperatureScales.Celsius,
                new: true
            });
        },
        edit(item) {
            this.cacheKey = +new Date();
            this.selected = { ...item };
            this.showModal = true;
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        async editSubscribers(item) {
            await this.$refs.subscribersDialog.show(
                async () => {
                    return await MineAreas.getSubscribersById(item.id);
                },
                async (subscribers) => {
                    await MineAreas.updateSubscribers({
                        _type: 'UpdateMineAreaNotificationSubscribersCommand',
                        mineAreaId: item.id,
                        subscribers: subscribers
                    });
                });
        },
        editShiftSchedule(item) {
            this.$refs.shiftScheduleEditor.open(item.id);
        },
        async del() {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Mine Area'), {})) {
                await MineAreas.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Mine Area')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        },
        showChangeTemperatureScale() {
            if (this.selected == null)
                return;

            this.editingTemperatureScale = this.selected.temperatureScale;
            this.showChangeTemperatureScaleModal = true;
        },
        async changeTemperatureScale() {
            if (!this.$refs.tempScaleForm.validate() || !this.selected) {
                return;
            }

            this.$wait.start('updating temp scale');
            try{
                await MineAreas.updateTemperatureScale({
                    _type: 'UpdateMineAreaTemperatureScaleCommand',
                    mineAreaId: this.selected.id,
                    temperatureScale: this.editingTemperatureScale
                });
                this.selected.temperatureScale = this.editingTemperatureScale;
                const existingMineArea = this.data.find(x=>x.id === this.selected.id);
                if(existingMineArea != null) {
                    existingMineArea.temperatureScale = this.editingTemperatureScale;
                }
                this.showChangeTemperatureScaleModal = false;
                EventBus.$emit(Events.ToastSuccess, this.$t('mine-areas.temperature-scale-updated'));
            } catch(e) {
                EventBus.$emit(Events.ToastError, this.$t('mine-areas.failed-to-update-temperature-scale'));
            }
            finally {
                this.$wait.end('updating temp scale');
            }
        },
        async save() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (this.selected.id == null) {
                await MineAreas.create({
                    _type: 'CreateMineAreaCommand',
                    name: this.selected.name,
                    numberOfShiftsPerDay: this.selected.numberOfShiftsPerDay,
                    firstShiftStartsAt: this.selected.firstShiftStartsAt,
                    shiftOverlapMinutes: this.selected.shiftOverlapMinutes,
                    firstDayOfWeek: this.selected.firstDayOfWeek,
                    startWeekCountFrom: this.selected.startWeekCountFrom,
                    timeZone: this.selected.timeZone,
                    shiftNameFirst: this.selected.shiftNameFirst,
                    shiftNameSecond: this.selected.shiftNameSecond,
                    shiftNameThird: this.selected.shiftNameThird,
                    shiftNameFourth: this.selected.shiftNameFourth,
                    locationConvention: this.selected.locationConvention,
                    temperatureScale: this.selected.temperatureScale
                });
            } else {
                await MineAreas.update({
                    _type: 'UpdateMineAreaCommand',
                    id: this.selected.id,
                    name: this.selected.name,
                    startWeekCountFrom: this.selected.startWeekCountFrom,
                    locationConvention: this.selected.locationConvention
                });
            }

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Mine Area')} Saved`);
            this.showModal = false;
            await this.loadData();
            await MineAreaStore.updateStoredMineAreas();
        },
        locationTestValid() {
            return Locations.validate(this.selected.locationConvention, this.testpattern);
        },
        async setImage(e) {
            const file = e.target.files[0];

            if (!file.type.includes('image/png')) {
                EventBus.$emit(Events.ToastError, this.$t('mine-areas.please-select-image-or-pdf-file'));
                return;
            }
            try {
                this.$wait.start('uploading logo');
                await AppHttp.postWithFiles(
                    '/Images/',
                    { name: this.selected.id + '_logo.png', parent: `${this.selected.id}` },
                    [file]
                );
                this.cacheKey = +new Date();
            } finally {
                this.$wait.end('uploading logo');
            }
        }
    }
};
</script>
<style lang='scss' scoped>
.mono-help {
    font-size: 0.8rem;
}
</style>
